/**
 * =============================================================================
 * IDS CRM Web - useSavedLogWork: the saved Log Work engine (Phase 6 step 7)
 * =============================================================================
 *
 * Spec 6.2. One saved Log Work per visit, shared by the crew. This hook loads
 * it (GET /api/v1/tasks/:id/log-work), saves each part the engineer changes
 * (PUT .../log-work/parts/:partKey, compare-and-set on the part's version,
 * with a fresh client_change_id), uploads photos one at a time (POST
 * .../log-work/files), and keeps every unsent change on the phone first
 * (utils/logWorkPhoneStore.js), so nothing is lost with no signal.
 *
 *   const saved = useSavedLogWork({ authFetch, taskId, userId, enabled });
 *
 * Status:
 *  - 'loading'      the first GET is on its way.
 *  - 'offline'      a network failure, or the GET took more than 5 s. The
 *                   window shows what the phone has; changes are still kept
 *                   (draft 'none' with openedAt) and the GET is retried.
 *  - 'unavailable'  ONLY on an explicit answer: 404, 503, or a 200 without
 *                   available: true (the {} every existing test mock gives),
 *                   or any other 4xx. Everything then behaves exactly as
 *                   before saved Log Work. A 503 on any later call switches
 *                   to it too. Also the status while enabled is false.
 *  - 'ready'        loaded.
 *
 * Rules this hook keeps (spec 0, 6.2):
 *  - Only savePart saves. Loading, pre-fill, defaults and restored values
 *    never call the server: opening then closing Log Work sends no PUT.
 *  - A change goes to the phone first, then (1.5 s later, or at once with
 *    { immediate: true }, on flush, and when the page is hidden) to the
 *    server. A PUT whose reply was lost is re-sent with the SAME
 *    client_change_id and value first, so it is never a conflict with
 *    yourself; only then is a newer value sent.
 *  - A network error keeps the change and retries after 5 s, 15 s, 60 s,
 *    then every 60 s, and when the browser comes back online.
 *  - 409 PART_NEWER (and a work-type change that would clear answers) is a
 *    conflict for the person to resolve. 409 LOG_WORK_ALREADY_SAVED,
 *    LOG_WORK_CLEARED or JOB_FINISHED make the change a stale copy, never
 *    applied by itself. Any other 4xx (not 401) marks it failed, with the
 *    server's sentence; a failed item is never retried and is not pending.
 *  - Remote changes: the GET is polled every 60 s while the page is visible.
 *    A part with no local unsent change takes the newer server version, and
 *    onRemoteParts(keys, fileKeys) is told. A part counts as this phone's own
 *    only at a version one of its PUT replies gave; the same person's change
 *    on another phone is a remote change like a colleague's.
 *
 * What it returns is listed at the foot of this file (useSavedLogWork).
 * =============================================================================
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getLogWorkPhoneStore, draftKeyOf, NO_PHONE_STORE_MESSAGE, PHONE_FULL_MESSAGE
} from '../utils/logWorkPhoneStore';
import { validateAttachment } from '../utils/attachments';
import { formatTime } from '../utils/format';
import { ukTodayKey } from '../utils/dates';
import { shortDayLabel } from '../utils/returnVisits';

export const DEBOUNCE_MS = 1500;
export const RETRY_DELAYS_MS = [5000, 15000, 60000];
export const POLL_MS = 60000;
export const OPEN_TIMEOUT_MS = 5000;

export const NO_SIGNAL_MESSAGE = 'Not saved - no signal. Your changes are kept on this phone and sent when you are back online.';
export const OFFLINE_OPEN_MESSAGE = 'No signal - showing what this phone has. Changes are kept and sent when you are back online.';
export const NO_SIGNAL_SAVE_MESSAGE = 'No signal. Your Log Work is kept on this phone; save again when you have signal.';
export const SEND_FAILED_MESSAGE = 'This part of Log Work could not be saved. It is kept on your phone; try again.';
export const FAILED_PHOTOS_MESSAGE = 'Remove the photos marked Failed, then save again.';

const STALE_CODES = ['LOG_WORK_ALREADY_SAVED', 'LOG_WORK_CLEARED', 'JOB_FINISHED'];

const PART_NAMES = {
  work: 'the work type',
  notes: 'the notes',
  outcome: 'the outcome',
  crew: 'the crew',
  van_check: 'the van check',
  second_van: 'the second van check',
  'form:task': 'the check form'
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const lower = (value) => String(value ?? '').trim().toLowerCase();
const isArchiveKey = (key) => String(key).startsWith('archive:');
const isRobotKey = (key) => String(key).startsWith('robot:');
const isFormKey = (key) => isRobotKey(key) || key === 'form:task';
// Spare parts (Push 2, spec 6.1): one Parts used line is one spare:<uuid>
// part. Never archived, never carried to the next day.
const isSpareKey = (key) => String(key).startsWith('spare:');
const sameJson = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const clone = (value) => (value === undefined ? null : JSON.parse(JSON.stringify(value)));
const nowIso = () => new Date().toISOString();
const timeOf = (value) => {
  if (!value) return NaN;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? NaN : ms;
};
const capitalise = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text);
const hasAnswers = (value) => Boolean(value && value.form && value.form.answers
  && typeof value.form.answers === 'object' && Object.keys(value.form.answers).length > 0);

/** A version 4 UUID: the server checks the shape, not the version. */
export function newUuid() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    try { return cryptoApi.randomUUID(); } catch (error) { /* not a secure context: fall through */ }
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function ukWeekday(date) {
  try {
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'Europe/London' }).format(date);
  } catch (error) {
    return '';
  }
}

/** '11:02' on today's UK day, otherwise 'on Tue 22 Sep at 11:02' (as the server words it). */
export function logWorkWhen(value, now = new Date()) {
  const ms = timeOf(value);
  if (!Number.isFinite(ms)) return '';
  const date = new Date(ms);
  const clock = formatTime(date);
  const key = ukTodayKey(date);
  if (key === ukTodayKey(now)) return clock;
  const weekday = ukWeekday(date);
  return `on ${weekday ? `${weekday} ` : ''}${shortDayLabel(key)} at ${clock}`;
}

/** 'at 11:02' or 'on Tue 22 Sep at 11:02'. */
export function logWorkAtWhen(value, now = new Date()) {
  const when = logWorkWhen(value, now);
  if (!when) return '';
  return when.startsWith('on ') ? when : `at ${when}`;
}

/** '10:42' today, 'Tue 22 Sep 10:42' on another day. */
function shortWhen(value, now = new Date()) {
  const when = logWorkWhen(value, now);
  if (!when.startsWith('on ')) return when;
  return when.slice(3).replace(' at ', ' ');
}

/**
 * How a part is named to people: 'BR-002', 'the notes', 'the crew', and a
 * Parts used line by its part when its value is to hand ('the Parts used
 * line Resolver'), as the server words it.
 */
export function logWorkPartLabel(partKey, assetRefs = {}, value = null) {
  const key = String(partKey || '');
  if (isRobotKey(key)) return (assetRefs && assetRefs[key.slice(6)]) || 'a robot';
  if (key.startsWith('journey:')) return 'the Job Stages';
  if (isArchiveKey(key)) return 'answers kept for reference';
  if (isSpareKey(key)) return value && value.name ? `the Parts used line ${value.name}` : 'a Parts used line';
  return PART_NAMES[key] || key;
}

/**
 * The Parts used lines of a stale copy (a staleCopies entry) that hold a
 * part, as { key, name, itemNumber, quantity }: a removal made on the phone
 * (null) is not a line. For the job page's notice of parts this phone could
 * not record.
 */
export function staleSpareLines(copy) {
  return (copy && Array.isArray(copy.parts) ? copy.parts : [])
    .filter(entry => entry && isSpareKey(entry.partKey) && entry.value)
    .map(entry => ({
      key: entry.partKey,
      name: entry.value.name || 'A part',
      itemNumber: entry.value.item_number || null,
      quantity: entry.value.quantity
    }));
}

async function readJson(response) {
  if (!response || response.status === 204 || typeof response.json !== 'function') return {};
  try {
    const body = await response.json();
    return body && typeof body === 'object' ? body : {};
  } catch (error) {
    return {};
  }
}

function readBlobAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('The photo could not be read.'));
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });
}

// ---------------------------------------------------------------------------
// The engine: plain JavaScript, so its functions stay the same object for the
// life of the page. The hook below holds one and re-renders when it changes.
// ---------------------------------------------------------------------------

function freshCore(gen) {
  return {
    gen,
    status: 'loading',
    draft: null,
    parts: {},
    files: [],
    unfiled: [],
    myUnfiled: [],
    leadLogs: [],
    lastSaved: null,
    jobFinished: false,
    canClear: false,
    draftId: null,
    openedAt: nowIso(),
    local: {},
    restoredOlder: [],
    conflicts: {},
    stale: [],
    photos: [],
    removals: [],
    sending: new Map(),
    photoBusy: false,
    timers: { debounce: {}, retry: null, poll: null },
    retryAttempt: 0,
    saveState: { lastSavedAt: null, sending: false, offline: false, message: null },
    remoteChanges: null,
    phoneMessage: null,
    listeners: null,
    // 'draftId|partKey' -> the versions this phone's own PUTs were given. A
    // part the server shows at any other version was written elsewhere (a
    // colleague, or the same person on another phone), so it is a remote
    // change, never "mine".
    written: new Map()
  };
}

/**
 * @param {Function} getProps  () => { authFetch, taskId, userId, userName, enabled, phoneStore,
 *                             assetRefs, onRemoteParts, timing: { debounceMs, retryDelaysMs, pollMs, openTimeoutMs } }
 * @param {Function} onChange  Called whenever anything the screen shows changed.
 */
export function createSavedLogWorkEngine(getProps, onChange = () => {}) {
  let core = freshCore(0);
  core.status = 'unavailable';

  const props = () => getProps() || {};
  const phone = () => props().phoneStore || getLogWorkPhoneStore();
  const timing = () => ({
    debounceMs: DEBOUNCE_MS,
    retryDelaysMs: RETRY_DELAYS_MS,
    pollMs: POLL_MS,
    openTimeoutMs: OPEN_TIMEOUT_MS,
    ...(props().timing || {})
  });
  const me = () => lower(props().userId);
  const emit = () => { try { onChange(); } catch (error) { /* a render problem must not stop a save */ } };
  const labelFor = (key, value = null) => logWorkPartLabel(key, props().assetRefs || {}, value);
  const lastSavedId = () => (core.lastSaved && core.lastSaved.draft_id) || null;

  // -------------------------------------------------------------------------
  // Talking to the server
  // -------------------------------------------------------------------------

  async function api(path, options) {
    const { authFetch, taskId } = props();
    try {
      const response = await authFetch(`/api/v1/tasks/${taskId}/log-work${path}`, options);
      const status = typeof response.status === 'number' ? response.status : (response.ok ? 200 : 500);
      const body = await readJson(response);
      return { ok: Boolean(response.ok), status, body };
    } catch (error) {
      return { network: true, error };
    }
  }

  function kindOf(result) {
    if (result.network) return 'network';
    if (result.status === 503) return 'unavailable';
    if (result.ok) return 'ok';
    if (result.status === 409) return 'conflict';
    if (result.status === 401) return 'auth';
    if (result.status >= 400 && result.status < 500) return 'refused';
    return 'error';
  }

  function clearTimers() {
    Object.values(core.timers.debounce).forEach(id => clearTimeout(id));
    core.timers.debounce = {};
    if (core.timers.retry) clearTimeout(core.timers.retry);
    core.timers.retry = null;
    if (core.timers.poll) clearInterval(core.timers.poll);
    core.timers.poll = null;
  }

  function goUnavailable() {
    clearTimers();
    core.status = 'unavailable';
    core.saveState = { ...core.saveState, sending: false, offline: false, message: null };
    emit();
  }

  function scheduleRetry() {
    if (core.timers.retry || core.status === 'unavailable') return;
    const delays = timing().retryDelaysMs;
    const delay = delays[Math.min(core.retryAttempt, delays.length - 1)];
    core.retryAttempt += 1;
    const gen = core.gen;
    core.timers.retry = setTimeout(() => {
      if (gen !== core.gen) return;
      core.timers.retry = null;
      retryNow();
    }, delay);
  }

  function markOffline() {
    core.saveState = { ...core.saveState, offline: true, message: NO_SIGNAL_MESSAGE };
    scheduleRetry();
    emit();
  }

  function markBackOnline() {
    core.retryAttempt = 0;
    if (core.saveState.offline || core.saveState.message === NO_SIGNAL_MESSAGE || core.saveState.message === SEND_FAILED_MESSAGE) {
      core.saveState = { ...core.saveState, offline: false, message: null };
    }
  }

  async function retryNow() {
    if (core.status === 'unavailable') return;
    if (core.status !== 'ready') {
      await fetchServer();
      return;
    }
    await flush();
  }

  // -------------------------------------------------------------------------
  // The phone copy
  // -------------------------------------------------------------------------

  async function persistEntry(entry) {
    const { userId, taskId } = props();
    const store = phone();
    try {
      if (entry.storedDraftId !== undefined && (entry.storedDraftId || null) !== (entry.draftId || null)) {
        await store.deletePart(userId, taskId, entry.storedDraftId, entry.partKey);
      }
      const result = await store.putPart({
        userId,
        taskId,
        draftId: entry.draftId || null,
        partKey: entry.partKey,
        value: entry.value,
        baseVersion: entry.baseVersion,
        clientChangeId: entry.unconfirmed ? entry.unconfirmed.clientChangeId : null,
        sentValue: entry.unconfirmed ? entry.unconfirmed.value : null,
        hasSent: Boolean(entry.unconfirmed),
        clearsRobotAnswers: Boolean(entry.clearsRobotAnswers),
        changedAt: entry.changedAt,
        openedAt: entry.openedAt,
        state: entry.state === 'failed' ? 'failed' : 'pending',
        failMessage: entry.failMessage || null
      });
      entry.storedDraftId = entry.draftId || null;
      if (result && result.ok === false && result.error === 'quota') {
        core.saveState = { ...core.saveState, message: PHONE_FULL_MESSAGE };
        emit();
      }
      return result || { ok: true };
    } catch (error) {
      return { ok: false, error: 'failed' };
    }
  }

  async function forgetEntry(entry) {
    const { userId, taskId } = props();
    try {
      await phone().deletePart(userId, taskId, entry.storedDraftId === undefined ? entry.draftId : entry.storedDraftId, entry.partKey);
    } catch (error) {
      // The copy stays on the phone; the next open finds the server already has it.
    }
  }

  async function persistPhoto(photo, state = 'pending') {
    const { userId, taskId } = props();
    try {
      return await phone().putPhoto({
        clientRef: photo.clientRef,
        userId,
        taskId,
        draftId: photo.draftId || null,
        partKey: photo.partKey,
        stepKey: photo.stepKey || null,
        name: photo.name,
        type: photo.type,
        size: photo.size,
        blob: photo.blob,
        addedAt: photo.addedAt,
        state,
        failMessage: photo.failMessage || null
      });
    } catch (error) {
      return { ok: false, error: 'failed' };
    }
  }

  async function forgetPhoto(clientRef) {
    try { await phone().deletePhoto(clientRef); } catch (error) { /* kept; harmless */ }
  }

  function entryFromRow(row) {
    return {
      partKey: row.partKey,
      value: row.value === undefined ? null : row.value,
      baseVersion: Number(row.baseVersion) || 0,
      draftId: row.draftId || null,
      storedDraftId: row.draftId || null,
      openedAt: row.openedAt || row.changedAt || null,
      changedAt: row.changedAt || null,
      state: row.state === 'failed' ? 'failed' : 'dirty',
      failMessage: row.failMessage || null,
      clearsRobotAnswers: Boolean(row.clearsRobotAnswers),
      unconfirmed: row.hasSent && row.clientChangeId
        ? { clientChangeId: row.clientChangeId, value: row.sentValue === undefined ? null : row.sentValue }
        : null,
      restored: true
    };
  }

  function photoFromRow(row) {
    return {
      clientRef: row.clientRef,
      draftId: row.draftId || null,
      partKey: row.partKey,
      stepKey: row.stepKey || null,
      name: row.name,
      type: row.type,
      size: row.size,
      blob: row.blob,
      addedAt: row.addedAt,
      state: row.state === 'failed' ? 'failed' : 'waiting',
      failMessage: row.failMessage || null
    };
  }

  /** Reads what the phone kept for this job, before the first GET answers. */
  async function restoreFromPhone(gen) {
    const { userId, taskId } = props();
    const store = phone();
    let parts = [];
    let photos = [];
    try { parts = await store.getParts(userId, taskId); } catch (error) { parts = []; }
    try { photos = await store.listPhotos(userId, taskId); } catch (error) { photos = []; }
    if (gen !== core.gen) return;

    const staleRows = [];
    const newest = {};
    for (const row of parts || []) {
      if (!row || !row.partKey) continue;
      if (row.state === 'stale') { staleRows.push(row); continue; }
      const current = newest[row.partKey];
      if (!current || String(row.changedAt || '') > String(current.changedAt || '')) {
        if (current) core.restoredOlder.push(entryFromRow(current));
        newest[row.partKey] = row;
      } else {
        core.restoredOlder.push(entryFromRow(row));
      }
    }
    for (const row of Object.values(newest)) {
      // A change made in this session already is newer than the phone's.
      if (!core.local[row.partKey]) core.local[row.partKey] = entryFromRow(row);
    }
    for (const row of photos || []) {
      if (!row || !row.clientRef) continue;
      if (row.state === 'stale') {
        addStale({ draftId: row.draftId || null, code: null, message: row.failMessage || null, photos: [photoFromRow(row)] });
        continue;
      }
      if (!core.photos.some(photo => photo.clientRef === row.clientRef)) core.photos.push(photoFromRow(row));
    }
    for (const row of staleRows) {
      addStale({ draftId: row.draftId || null, code: null, message: row.failMessage || null, parts: [entryFromRow(row)] });
    }
    try {
      const persistent = typeof store.ready === 'function' ? await store.ready() : true;
      if (gen !== core.gen) return;
      core.phoneMessage = persistent ? null : (store.message || NO_PHONE_STORE_MESSAGE);
    } catch (error) {
      core.phoneMessage = NO_PHONE_STORE_MESSAGE;
    }
    emit();
  }

  // -------------------------------------------------------------------------
  // Stale copies
  // -------------------------------------------------------------------------

  function addStale({ draftId, code, message, parts = [], photos = [] }) {
    const id = draftKeyOf(draftId);
    let copy = core.stale.find(item => item.id === id);
    if (!copy) {
      copy = { id, draftId: draftId || null, code: code || null, serverMessage: message || null, parts: [], photos: [], shown: false };
      core.stale.push(copy);
    }
    if (code && !copy.code) copy.code = code;
    if (message && !copy.serverMessage) copy.serverMessage = message;
    for (const entry of parts) {
      copy.parts = copy.parts.filter(item => item.partKey !== entry.partKey);
      copy.parts.push(entry);
    }
    for (const photo of photos) {
      if (!copy.photos.some(item => item.clientRef === photo.clientRef)) copy.photos.push(photo);
    }
    return copy;
  }

  /** Moves every unsent change of one draft (or of 'none') out of the way, as a stale copy. */
  async function moveDraftToStale(draftId, code, message) {
    const key = draftKeyOf(draftId);
    const entries = Object.values(core.local).filter(entry => draftKeyOf(entry.draftId) === key && entry.state !== 'failed');
    const photos = core.photos.filter(photo => draftKeyOf(photo.draftId) === key && photo.state !== 'sending');
    for (const entry of entries) {
      if (core.timers.debounce[entry.partKey]) {
        clearTimeout(core.timers.debounce[entry.partKey]);
        delete core.timers.debounce[entry.partKey];
      }
      delete core.local[entry.partKey];
      delete core.conflicts[entry.partKey];
    }
    core.photos = core.photos.filter(photo => !photos.includes(photo));
    addStale({ draftId, code, message, parts: entries, photos });
    for (const entry of entries) {
      entry.state = 'stale';
      await markStaleOnPhone(entry, message);
    }
    for (const photo of photos) await persistPhoto({ ...photo, failMessage: message || null }, 'stale');
    emit();
  }

  async function markStaleOnPhone(entry, message) {
    const { userId, taskId } = props();
    try {
      await phone().putPart({
        userId,
        taskId,
        draftId: entry.draftId || null,
        partKey: entry.partKey,
        value: entry.value,
        baseVersion: entry.baseVersion,
        clientChangeId: null,
        sentValue: null,
        hasSent: false,
        changedAt: entry.changedAt,
        openedAt: entry.openedAt,
        state: 'stale',
        failMessage: message || null
      });
      entry.storedDraftId = entry.draftId || null;
    } catch (error) {
      // Kept in memory for this session.
    }
  }

  function staleMessage(copy) {
    const saved = core.lastSaved;
    if (copy.code === 'JOB_FINISHED') return 'Changes made on this phone after the job was finished were not filed.';
    if (saved && saved.saved_at && (!saved.cleared_at || timeOf(saved.saved_at) >= timeOf(saved.cleared_at))) {
      return `Changes made on this phone after ${saved.saved_by_name || 'someone'} saved Log Work ${logWorkAtWhen(saved.saved_at)} were not filed.`;
    }
    if (saved && saved.cleared_at) {
      return `Changes made on this phone after ${saved.cleared_by_name || 'someone'} cleared Log Work ${logWorkAtWhen(saved.cleared_at)} were not filed.`;
    }
    return 'Changes made on this phone were not filed.';
  }

  // -------------------------------------------------------------------------
  // Loading and refreshing
  // -------------------------------------------------------------------------

  function applyServer(body) {
    const draft = body.draft && typeof body.draft === 'object' ? body.draft : null;
    const incomingId = draft ? draft.draft_id : null;
    const incoming = draft && draft.parts && typeof draft.parts === 'object' ? draft.parts : {};
    const parts = {};
    for (const [key, part] of Object.entries(incoming)) {
      const previous = core.parts[key];
      // A GET that left before our own PUT answered must not take a part back.
      if (previous && incomingId && incomingId === core.draftId && Number(previous.version) > Number(part.version)) {
        parts[key] = previous;
      } else {
        parts[key] = { ...part };
      }
    }
    core.draft = draft;
    core.parts = parts;
    core.files = draft && Array.isArray(draft.files) ? [...draft.files] : [];
    core.draftId = incomingId;
    core.unfiled = Array.isArray(body.unfiled) ? body.unfiled : [];
    core.myUnfiled = Array.isArray(body.my_unfiled_journeys) ? body.my_unfiled_journeys : [];
    core.leadLogs = Array.isArray(body.lead_logs) ? body.lead_logs : [];
    core.lastSaved = body.last_saved || null;
    core.jobFinished = Boolean(body.job_finished);
    core.canClear = Boolean(body.can_clear);
  }

  /** After the first answer: the phone's copies become sends, conflicts or stale copies. */
  async function mergeLocal() {
    const activeId = core.draftId;
    const saved = core.lastSaved;
    const closedAt = saved ? Math.max(timeOf(saved.saved_at) || 0, timeOf(saved.cleared_at) || 0) : 0;
    const isStale = (draftId, madeAt) => {
      if (draftId) return draftId !== activeId;
      const made = timeOf(madeAt);
      return closedAt > 0 && Number.isFinite(made) && closedAt > made;
    };

    const staleByDraft = new Map();
    const noteStale = (draftId, item, isPhoto) => {
      const key = draftKeyOf(draftId);
      if (!staleByDraft.has(key)) staleByDraft.set(key, { draftId, parts: [], photos: [] });
      staleByDraft.get(key)[isPhoto ? 'photos' : 'parts'].push(item);
    };

    for (const entry of core.restoredOlder) {
      if (isStale(entry.draftId, entry.openedAt)) noteStale(entry.draftId, entry, false);
      else forgetEntry(entry);
    }
    core.restoredOlder = [];

    for (const entry of Object.values(core.local)) {
      if (isStale(entry.draftId, entry.openedAt)) {
        delete core.local[entry.partKey];
        noteStale(entry.draftId, entry, false);
        continue;
      }
      if (entry.state === 'failed') continue;
      const serverPart = core.parts[entry.partKey];
      const serverVersion = serverPart ? Number(serverPart.version) || 0 : 0;
      if (serverPart && sameJson(serverPart.value, entry.value)) {
        delete core.local[entry.partKey];
        forgetEntry(entry);
        continue;
      }
      if (!entry.unconfirmed && serverPart && serverVersion > entry.baseVersion) {
        entry.state = 'conflict';
        core.conflicts[entry.partKey] = buildConflict(entry.partKey, entry, serverPart, 'part_newer', null);
      } else if (!entry.unconfirmed) {
        entry.baseVersion = serverVersion;
      }
      if (entry.draftId !== activeId) {
        entry.draftId = activeId;
        persistEntry(entry);
      }
    }

    const keptPhotos = [];
    for (const photo of core.photos) {
      if (photo.state !== 'sending' && isStale(photo.draftId, photo.addedAt)) {
        noteStale(photo.draftId, photo, true);
        continue;
      }
      if (photo.draftId !== activeId && photo.state !== 'sending') {
        photo.draftId = activeId;
        persistPhoto(photo, photo.state === 'failed' ? 'failed' : 'pending');
      }
      keptPhotos.push(photo);
    }
    core.photos = keptPhotos;

    for (const group of staleByDraft.values()) {
      addStale({ draftId: group.draftId, code: null, message: null, parts: group.parts, photos: group.photos });
      for (const entry of group.parts) await markStaleOnPhone(entry, null);
      for (const photo of group.photos) await persistPhoto(photo, 'stale');
    }
  }

  /** Local changes made against a draft that is no longer the active one. */
  async function checkDraftMoved() {
    const activeId = core.draftId;
    const movedIds = new Set();
    for (const entry of Object.values(core.local)) {
      if (entry.draftId && entry.draftId !== activeId && entry.state !== 'failed') movedIds.add(entry.draftId);
    }
    for (const draftId of movedIds) await moveDraftToStale(draftId, null, null);
  }

  function noteWritten(draftId, key, version) {
    if (!draftId || !Number.isFinite(Number(version))) return;
    const id = `${lower(draftId)}|${key}`;
    if (!core.written.has(id)) core.written.set(id, new Set());
    core.written.get(id).add(Number(version));
  }

  /**
   * True only when the server's part is one this phone wrote: the version a
   * PUT reply gave this phone, or (a reply lost on the way) the exact value
   * still waiting for its reply. The same person on another phone is not mine.
   */
  function isMine(key, after) {
    if (!after || lower(after.changed_by) !== me()) return false;
    const versions = core.written.get(`${lower(core.draftId)}|${key}`);
    if (versions && versions.has(Number(after.version))) return true;
    const entry = core.local[key];
    if (entry && entry.unconfirmed) {
      const sent = entry.unconfirmed.value;
      return sameJson(after.value, sent) || sameJson(after.value, asStored(key, sent));
    }
    return false;
  }

  function diffAgainst(previous) {
    const changed = [];
    const phrases = [];
    const myId = me();
    if (previous.draftId && previous.draftId !== core.draftId) {
      const saved = core.lastSaved;
      let phrase = 'This Log Work was saved or cleared on another phone';
      if (saved && saved.draft_id === previous.draftId && saved.saved_at) {
        phrase = `${saved.saved_by_name || 'Someone'} saved this Log Work as hours ${logWorkAtWhen(saved.saved_at)}`;
      } else if (saved && saved.draft_id === previous.draftId && saved.cleared_at) {
        phrase = `${saved.cleared_by_name || 'Someone'} cleared this Log Work ${logWorkAtWhen(saved.cleared_at)}`;
      }
      changed.push({ kind: 'draft', part_key: null, label: 'the saved Log Work', changed_by_name: null, changed_at: null });
      phrases.push(phrase);
    } else {
      const keys = new Set([...Object.keys(previous.parts), ...Object.keys(core.parts)]);
      for (const key of keys) {
        const before = previous.parts[key];
        const after = core.parts[key];
        if (before && after && Number(before.version) === Number(after.version)) continue;
        if (!after) continue;
        // A Parts used line a colleague removed is still named by its part.
        const label = labelFor(key, after.value || (before && before.value) || null);
        const mine = isMine(key, after);
        changed.push({
          kind: 'part', part_key: key, label, changed_by: after.changed_by || null,
          changed_by_name: after.changed_by_name || null, changed_at: after.changed_at || null,
          mine
        });
        // The same person on another phone is told it was them.
        const who = !mine && myId && lower(after.changed_by) === myId
          ? 'you'
          : (after.changed_by_name || 'Someone');
        const where = who === 'you' ? ' on another phone or computer' : '';
        phrases.push(`${who} changed ${label}${where} ${logWorkAtWhen(after.changed_at)}`.trim());
      }
      const beforeIds = new Set(previous.files.map(file => file.file_id));
      const afterIds = new Set(core.files.map(file => file.file_id));
      for (const file of core.files) {
        if (beforeIds.has(file.file_id)) continue;
        changed.push({ kind: 'file', part_key: file.part_key || null, label: 'a photo', changed_by_name: file.uploaded_by_name || null, changed_at: file.uploaded_at || null });
        phrases.push(`${file.uploaded_by_name || 'Someone'} added a photo ${logWorkAtWhen(file.uploaded_at)}`.trim());
      }
      const removed = previous.files.filter(file => !afterIds.has(file.file_id));
      if (removed.length > 0) {
        removed.forEach(file => {
          changed.push({ kind: 'file', part_key: file.part_key || null, label: 'a photo', changed_by_name: null, changed_at: null });
        });
        phrases.push('a photo was removed');
      }
    }
    const message = phrases.length > 0 ? `${capitalise(phrases.join(' and '))}. Check it, then save again.` : null;
    return { changed, message };
  }

  /**
   * GET the saved Log Work. The first answer turns loading/offline into ready
   * and merges the phone's copy; later answers are refreshes.
   */
  async function fetchServer() {
    if (core.status === 'unavailable') return { ok: false, unavailable: true, changed: [], message: null };
    const gen = core.gen;
    const { openTimeoutMs } = timing();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timer = null;
    const timeout = new Promise(resolve => {
      timer = setTimeout(() => resolve({ network: true, timeout: true }), openTimeoutMs);
    });
    const result = await Promise.race([api('', controller ? { signal: controller.signal } : undefined), timeout]);
    clearTimeout(timer);
    if (result.timeout && controller) {
      try { controller.abort(); } catch (error) { /* already finished */ }
    }
    if (gen !== core.gen) return { ok: false, changed: [], message: null, reset: true };

    if (result.network) {
      if (core.status === 'loading' || core.status === 'offline') core.status = 'offline';
      markOffline();
      return { ok: false, offline: true, changed: [], message: NO_SIGNAL_SAVE_MESSAGE };
    }
    const { status, body } = result;
    if (!result.ok && status >= 500 && status !== 503) {
      if (core.status === 'loading') core.status = 'offline';
      core.saveState = { ...core.saveState, message: body.message || 'Log Work could not be opened. Try again.' };
      scheduleRetry();
      emit();
      return { ok: false, error: true, changed: [], message: core.saveState.message };
    }
    if (!result.ok || body.available !== true) {
      goUnavailable();
      return { ok: false, unavailable: true, changed: [], message: null };
    }

    const previous = { parts: core.parts, files: core.files, draftId: core.draftId };
    const wasReady = core.status === 'ready';
    applyServer(body);
    markBackOnline();
    core.status = 'ready';
    ensurePoll();

    let diff = { changed: [], message: null };
    if (!wasReady) {
      await mergeLocal();
      if (gen !== core.gen) return { ok: false, changed: [], message: null, reset: true };
    } else {
      diff = diffAgainst(previous);
      await checkDraftMoved();
      if (gen !== core.gen) return { ok: false, changed: [], message: null, reset: true };
      const remoteKeys = diff.changed.filter(item => item.kind === 'part' && !item.mine).map(item => item.part_key);
      // A photo added or removed elsewhere changes no part: the screen is told
      // which parts' photos to show again.
      const fileKeys = [...new Set(diff.changed
        .filter(item => item.kind === 'file' && item.part_key)
        .map(item => item.part_key))];
      if (remoteKeys.length > 0 || fileKeys.length > 0) {
        core.remoteChanges = { keys: remoteKeys, fileKeys, at: nowIso() };
        const { onRemoteParts } = props();
        if (typeof onRemoteParts === 'function') {
          try { onRemoteParts(remoteKeys, fileKeys); } catch (error) { /* the screen's own problem */ }
        }
      }
    }
    emit();
    // Anything the phone still holds goes now.
    sendAllDirty().then(results => {
      if (results.some(outcome => outcome === 'offline' || outcome === 'error')) scheduleRetry();
    });
    pumpPhotos();
    const remote = diff.changed.filter(item => !item.mine);
    return {
      ok: true,
      changed: remote,
      message: remote.length > 0 ? diff.message : null
    };
  }

  function ensurePoll() {
    if (core.timers.poll) return;
    const gen = core.gen;
    core.timers.poll = setInterval(() => {
      if (gen !== core.gen || core.status !== 'ready') return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (core.sending.size > 0 || core.photoBusy) return;
      fetchServer();
    }, timing().pollMs);
  }

  // -------------------------------------------------------------------------
  // Parts
  // -------------------------------------------------------------------------

  function buildConflict(key, entry, theirs, kind, serverBody) {
    // A Parts used line is named by its part: theirs, or this phone's when
    // the colleague removed it (as the server's sentence does).
    const label = labelFor(key, (theirs && theirs.value) || (entry && entry.value) || null);
    const theirName = (theirs && theirs.changed_by_name) || 'A colleague';
    const when = theirs ? shortWhen(theirs.changed_at) : '';
    let message;
    if (kind === 'work_type') {
      message = (serverBody && serverBody.message) || 'Changing the work type clears the check form answers. Change it anyway?';
    } else if (isFormKey(key)) {
      message = `${capitalise(label)} - filled in by ${theirName}${when ? `, ${when}` : ''}`;
    } else {
      message = `${capitalise(label)} - changed by ${theirName}${when ? `, ${when}` : ''}`;
    }
    return {
      partKey: key,
      kind,
      label,
      message,
      serverMessage: serverBody && serverBody.message ? serverBody.message : null,
      theirName,
      theirs: theirs ? { ...theirs } : null,
      robots: serverBody && Array.isArray(serverBody.robots) ? serverBody.robots : [],
      canKeepBoth: key === 'notes' && kind === 'part_newer'
    };
  }

  /**
   * What the server keeps for a part it adjusted: the outcome's "back to
   * me", and who added a Parts used line (kept when a colleague changes it).
   */
  function asStored(key, value) {
    if (!value || typeof value !== 'object') return value;
    if (isSpareKey(key)) return { ...value, added_by: value.added_by || me() };
    if (key !== 'outcome') return value;
    return { ...value, self_user_id: value.assign_next_type === 'self' ? me() : null };
  }

  function learnDraftId(draftId) {
    if (!draftId || draftId === core.draftId) return;
    core.draftId = draftId;
    for (const entry of Object.values(core.local)) {
      if (!entry.draftId) {
        entry.draftId = draftId;
        persistEntry(entry);
      }
    }
    for (const photo of core.photos) {
      if (!photo.draftId && photo.state !== 'sending') {
        photo.draftId = draftId;
        persistPhoto(photo, photo.state === 'failed' ? 'failed' : 'pending');
      }
    }
  }

  function sendPart(key) {
    if (core.sending.has(key)) return core.sending.get(key);
    const promise = sendPartNow(key).finally(() => {
      core.sending.delete(key);
      core.saveState = { ...core.saveState, sending: core.sending.size > 0 || core.photoBusy };
      emit();
    });
    core.sending.set(key, promise);
    core.saveState = { ...core.saveState, sending: true };
    emit();
    return promise;
  }

  async function sendPartNow(key) {
    const gen = core.gen;
    for (let round = 0; round < 20; round += 1) {
      const entry = core.local[key];
      if (!entry) return 'saved';
      if (entry.state !== 'dirty') return entry.state;
      if (core.status !== 'ready') return 'wait';

      let sendValue;
      let changeId;
      if (entry.unconfirmed) {
        sendValue = entry.unconfirmed.value;
        changeId = entry.unconfirmed.clientChangeId;
      } else {
        sendValue = clone(entry.value);
        changeId = newUuid();
        entry.unconfirmed = { clientChangeId: changeId, value: sendValue };
        await persistEntry(entry);
        if (gen !== core.gen) return 'reset';
      }

      const body = {
        draft_id: core.draftId,
        seen_last_saved_id: lastSavedId(),
        base_version: entry.baseVersion,
        value: sendValue,
        client_change_id: changeId
      };
      if (entry.clearsRobotAnswers) body.clears_robot_answers = true;

      const result = await api(`/parts/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(body) });
      if (gen !== core.gen) return 'reset';
      const kind = kindOf(result);

      if (kind === 'network') {
        markOffline();
        return 'offline';
      }
      if (kind === 'unavailable') {
        goUnavailable();
        return 'unavailable';
      }
      if (kind === 'auth') return 'wait';

      if (kind === 'ok') {
        const answer = result.body || {};
        entry.unconfirmed = null;
        entry.clearsRobotAnswers = false;
        const version = Number(answer.version);
        if (answer.draft_id) {
          learnDraftId(answer.draft_id);
          entry.draftId = answer.draft_id;
          entry.baseVersion = Number.isFinite(version) ? version : entry.baseVersion;
          core.parts[key] = {
            ...(core.parts[key] || { carried: false }),
            value: asStored(key, sendValue),
            version: entry.baseVersion,
            changed_by: me(),
            changed_by_name: answer.changed_by_name || props().userName || null,
            changed_at: answer.changed_at || nowIso(),
            carried: false
          };
          noteWritten(answer.draft_id, key, entry.baseVersion);
          // A new draft, or my Job Stages record named: the GET brings the
          // draft and the record's times (journeys), which only a GET fills.
          if (answer.draft_created || key.startsWith('journey:')) quietRefresh();
        }
        markBackOnline();
        core.saveState = { ...core.saveState, lastSavedAt: answer.changed_at || nowIso() };
        if (core.local[key] === entry && sameJson(entry.value, sendValue)) {
          delete core.local[key];
          await forgetEntry(entry);
          emit();
          return 'saved';
        }
        if (core.local[key] !== entry) return 'saved';
        await persistEntry(entry);
        continue;
      }

      if (kind === 'conflict') {
        const answer = result.body || {};
        entry.unconfirmed = null;
        if (answer.code === 'PART_NEWER') {
          const current = answer.current || null;
          if (current) {
            core.parts[key] = {
              ...(core.parts[key] || { carried: false }),
              value: current.value,
              version: current.version,
              changed_by: null,
              changed_by_name: current.changed_by_name,
              changed_at: current.changed_at
            };
            if (sameJson(current.value, entry.value)) {
              // A colleague saved exactly this: nothing to choose.
              delete core.local[key];
              await forgetEntry(entry);
              emit();
              return 'saved';
            }
            entry.baseVersion = Number(current.version) || entry.baseVersion;
          }
          entry.state = 'conflict';
          core.conflicts[key] = buildConflict(key, entry, current, 'part_newer', answer);
          await persistEntry(entry);
          emit();
          return 'conflict';
        }
        if (answer.code === 'WORK_TYPE_CLEARS') {
          entry.state = 'conflict';
          core.conflicts[key] = buildConflict(key, entry, core.parts[key] || null, 'work_type', answer);
          await persistEntry(entry);
          emit();
          return 'conflict';
        }
        if (STALE_CODES.includes(answer.code)) {
          await moveDraftToStale(entry.draftId, answer.code, answer.message || null);
          quietRefresh();
          return 'stale';
        }
      }

      if (kind === 'conflict' || kind === 'refused') {
        const answer = result.body || {};
        entry.unconfirmed = null;
        entry.state = 'failed';
        entry.failMessage = answer.message || 'This change could not be saved.';
        await persistEntry(entry);
        emit();
        return 'failed';
      }

      // A server fault: kept, and tried again later.
      core.saveState = { ...core.saveState, message: (result.body && result.body.message) || SEND_FAILED_MESSAGE };
      scheduleRetry();
      emit();
      return 'error';
    }
    return 'error';
  }

  async function sendAllDirty() {
    const results = [];
    for (const key of Object.keys(core.local)) {
      const entry = core.local[key];
      if (!entry || entry.state !== 'dirty') continue;
      if (core.status !== 'ready') break;
      results.push(await sendPart(key));
    }
    return results;
  }

  function quietRefresh() {
    Promise.resolve().then(() => fetchServer()).catch(() => {});
  }

  /**
   * Saves one part. Only a person's own change should come here: pre-fill,
   * defaults and restored values never call it.
   *
   * @param {string} key      'work' | 'notes' | 'outcome' | 'crew' | 'van_check' | 'second_van' | 'form:task'
   *                          | 'robot:<asset_id>' | 'journey:<user_id>' | 'spare:<uuid>' (one Parts used line)
   * @param {object|null} value  The spec 2.2 shape; null empties the part.
   * @param {object} [options]
   * @param {boolean} [options.immediate]          Send now (robot folded, journey created).
   * @param {boolean} [options.clearsRobotAnswers] The work type change was confirmed.
   * @returns {Promise<{ok: boolean, unavailable?: boolean, message?: string}>}
   */
  async function savePart(key, value, options = {}) {
    const { enabled } = props();
    if (!enabled || core.status === 'unavailable') return { ok: false, unavailable: true };
    const partKey = lower(key);
    if (!partKey || isArchiveKey(partKey)) return { ok: false, message: 'That part of Log Work is not recognised.' };

    let entry = core.local[partKey];
    if (!entry) {
      const serverPart = core.parts[partKey];
      entry = {
        partKey,
        value: null,
        baseVersion: serverPart ? Number(serverPart.version) || 0 : 0,
        draftId: core.draftId,
        storedDraftId: undefined,
        openedAt: core.openedAt,
        changedAt: null,
        state: 'dirty',
        failMessage: null,
        clearsRobotAnswers: false,
        unconfirmed: null
      };
      core.local[partKey] = entry;
    }
    entry.value = clone(value);
    entry.changedAt = nowIso();
    if (options.clearsRobotAnswers) entry.clearsRobotAnswers = true;
    if (entry.state === 'failed') {
      entry.state = 'dirty';
      entry.failMessage = null;
    }
    emit();

    const stored = await persistEntry(entry);
    if (core.status !== 'ready' || entry.state !== 'dirty' || core.local[partKey] !== entry) {
      return { ok: stored.ok !== false, message: stored.message };
    }
    const timers = core.timers.debounce;
    if (timers[partKey]) {
      clearTimeout(timers[partKey]);
      delete timers[partKey];
    }
    if (options.immediate) {
      const outcome = await sendPart(partKey);
      return { ok: outcome === 'saved', outcome };
    }
    const gen = core.gen;
    timers[partKey] = setTimeout(() => {
      if (gen !== core.gen) return;
      delete core.timers.debounce[partKey];
      sendPart(partKey);
    }, timing().debounceMs);
    return { ok: stored.ok !== false, message: stored.message };
  }

  // -------------------------------------------------------------------------
  // Photos and files
  // -------------------------------------------------------------------------

  /**
   * Adds one photo or file: kept on the phone first, then sent (one at a
   * time) as a data URL. Resolves at once with a reference the screen keeps;
   * the photo's state is in photos / files, found by client_ref.
   *
   * @returns {Promise<{ok: boolean, ref?: {client_ref, part_key, step_key, name, size, type}, message?: string, unavailable?: boolean}>}
   */
  async function uploadFile({ partKey, stepKey = null, file } = {}) {
    const { enabled } = props();
    if (!enabled || core.status === 'unavailable') return { ok: false, unavailable: true };
    if (!file) return { ok: false, message: 'Choose a photo or file to add.' };
    const invalid = validateAttachment(file);
    if (invalid) return { ok: false, message: invalid };

    const photo = {
      clientRef: newUuid(),
      draftId: core.draftId,
      partKey: lower(partKey || 'attachments'),
      stepKey: stepKey || null,
      name: file.name,
      type: file.type,
      size: file.size,
      blob: file,
      addedAt: nowIso(),
      state: 'waiting',
      failMessage: null
    };
    const stored = await persistPhoto(photo, 'pending');
    if (stored && stored.ok === false && stored.error === 'quota') {
      return { ok: false, message: PHONE_FULL_MESSAGE };
    }
    core.photos.push(photo);
    emit();
    pumpPhotos();
    return {
      ok: true,
      ref: { client_ref: photo.clientRef, part_key: photo.partKey, step_key: photo.stepKey, name: photo.name, size: photo.size, type: photo.type }
    };
  }

  async function pumpPhotos() {
    if (core.photoBusy || core.status !== 'ready') return;
    const photo = core.photos.find(item => item.state === 'waiting');
    if (!photo) return;
    const gen = core.gen;
    core.photoBusy = true;
    photo.state = 'sending';
    core.saveState = { ...core.saveState, sending: true };
    emit();
    let outcome = 'next';
    try {
      outcome = await sendPhoto(photo, gen);
    } finally {
      if (gen === core.gen) {
        core.photoBusy = false;
        core.saveState = { ...core.saveState, sending: core.sending.size > 0 };
        emit();
      }
    }
    if (gen === core.gen && outcome === 'next') pumpPhotos();
  }

  async function sendPhoto(photo, gen) {
    let dataUrl;
    try {
      dataUrl = await readBlobAsDataUrl(photo.blob);
    } catch (error) {
      photo.state = 'failed';
      photo.failMessage = 'That file could not be read. Add it again.';
      await persistPhoto(photo, 'failed');
      return 'next';
    }
    if (gen !== core.gen) return 'reset';
    const result = await api('/files', {
      method: 'POST',
      body: JSON.stringify({
        draft_id: core.draftId,
        seen_last_saved_id: lastSavedId(),
        part_key: photo.partKey,
        step_key: photo.stepKey || undefined,
        client_ref: photo.clientRef,
        file: { dataUrl, name: photo.name }
      })
    });
    if (gen !== core.gen) return 'reset';
    const kind = kindOf(result);
    if (kind === 'network') {
      photo.state = 'waiting';
      markOffline();
      return 'stop';
    }
    if (kind === 'unavailable') {
      photo.state = 'waiting';
      goUnavailable();
      return 'stop';
    }
    if (kind === 'auth') {
      photo.state = 'waiting';
      return 'stop';
    }
    if (kind === 'ok') {
      const row = result.body || {};
      core.photos = core.photos.filter(item => item !== photo);
      await forgetPhoto(photo.clientRef);
      markBackOnline();
      if (photo.cancelled) {
        if (row.file_id) await removeServerFile(row.file_id);
        return 'next';
      }
      if (row.file_id && !core.files.some(file => file.file_id === row.file_id)) core.files.push(row);
      core.saveState = { ...core.saveState, lastSavedAt: row.uploaded_at || nowIso() };
      if (!core.draftId) quietRefresh();
      return 'next';
    }
    const answer = result.body || {};
    if (kind === 'conflict' && STALE_CODES.includes(answer.code)) {
      photo.state = 'waiting';
      await moveDraftToStale(photo.draftId, answer.code, answer.message || null);
      quietRefresh();
      return 'stop';
    }
    if (kind === 'conflict' || kind === 'refused') {
      photo.state = 'failed';
      photo.failMessage = answer.message || 'This photo could not be saved.';
      await persistPhoto(photo, 'failed');
      return 'next';
    }
    photo.state = 'waiting';
    core.saveState = { ...core.saveState, message: answer.message || 'The photo could not be saved. It is kept on your phone; try again.' };
    scheduleRetry();
    return 'stop';
  }

  async function removeServerFile(fileId) {
    core.files = core.files.filter(file => file.file_id !== fileId);
    emit();
    const result = await api(`/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' });
    const kind = kindOf(result);
    if (kind === 'network') {
      if (!core.removals.includes(fileId)) core.removals.push(fileId);
      markOffline();
      return { ok: false, offline: true, message: NO_SIGNAL_MESSAGE };
    }
    if (kind === 'unavailable') {
      goUnavailable();
      return { ok: false, unavailable: true };
    }
    core.removals = core.removals.filter(id => id !== fileId);
    if (kind === 'ok' || result.status === 404) return { ok: true };
    // Refused: show the server's list again, so nothing looks removed that is not.
    quietRefresh();
    return { ok: false, message: (result.body && result.body.message) || 'The photo could not be removed. Try again.' };
  }

  /** Removes a photo: one still on the phone (by client_ref), or one saved (by file_id or client_ref). */
  async function removeFile(idOrRef) {
    const id = String(idOrRef || '');
    const local = core.photos.find(photo => photo.clientRef === id);
    if (local) {
      if (local.state === 'sending') {
        local.cancelled = true;
      } else {
        core.photos = core.photos.filter(photo => photo !== local);
        await forgetPhoto(local.clientRef);
      }
      emit();
      return { ok: true };
    }
    const saved = core.files.find(file => file.file_id === id || file.client_ref === id);
    if (!saved) return { ok: true };
    return removeServerFile(saved.file_id);
  }

  async function sendRemovals() {
    const results = [];
    for (const fileId of [...core.removals]) {
      if (core.status !== 'ready') break;
      results.push(await removeServerFile(fileId));
    }
    return results;
  }

  // -------------------------------------------------------------------------
  // Flush, conflicts, stale copies, filing
  // -------------------------------------------------------------------------

  function failedItems() {
    const items = [];
    for (const entry of Object.values(core.local)) {
      if (entry.state === 'failed') {
        items.push({ kind: 'part', key: entry.partKey, label: labelFor(entry.partKey, entry.value), message: entry.failMessage });
      }
    }
    for (const photo of core.photos) {
      if (photo.state === 'failed') {
        items.push({ kind: 'photo', key: photo.clientRef, label: photo.name || 'a photo', message: photo.failMessage });
      }
    }
    return items;
  }

  const pendingPhotoCount = () => core.photos.filter(photo => photo.state === 'waiting' || photo.state === 'sending').length;

  /**
   * Sends everything now: parts (cancelling their debounce), removals, and
   * starts the photo queue. Resolves with whether Complete Work Log may go on.
   *
   * @returns {Promise<{ok, message, offline, unavailable, conflicts, failed, stale, pendingPhotos, failedPhotos}>}
   */
  async function flush() {
    const { enabled } = props();
    const summary = (extra) => ({
      ok: false,
      message: null,
      offline: false,
      unavailable: false,
      conflicts: Object.keys(core.conflicts).length,
      failed: failedItems().filter(item => item.kind === 'part').length,
      stale: false,
      pendingPhotos: pendingPhotoCount(),
      failedPhotos: core.photos.filter(photo => photo.state === 'failed').length,
      ...extra
    });
    if (!enabled || core.status === 'unavailable') return summary({ ok: true, unavailable: true });

    Object.values(core.timers.debounce).forEach(id => clearTimeout(id));
    core.timers.debounce = {};

    if (core.status !== 'ready') {
      return summary({ offline: true, message: NO_SIGNAL_SAVE_MESSAGE });
    }

    const results = await sendAllDirty();
    // A send started before this flush may still be on its way.
    for (const promise of [...core.sending.values()]) {
      results.push(await promise);
    }
    const removals = await sendRemovals();
    pumpPhotos();

    if (core.status === 'unavailable') return summary({ ok: true, unavailable: true });

    const conflicts = Object.values(core.conflicts);
    const failed = failedItems().filter(item => item.kind === 'part');
    const failedPhotos = core.photos.filter(photo => photo.state === 'failed').length;
    const pendingPhotos = pendingPhotoCount();
    const offline = results.includes('offline') || removals.some(item => item.offline);
    const stale = results.includes('stale');
    const errored = results.includes('error');
    const stillDirty = Object.values(core.local).some(entry => entry.state === 'dirty');

    let message = null;
    if (conflicts.length > 0) message = conflicts[0].serverMessage || conflicts[0].message;
    else if (stale) message = staleMessage(core.stale[core.stale.length - 1] || {});
    else if (failed.length > 0) message = failed[0].message;
    else if (offline) message = NO_SIGNAL_SAVE_MESSAGE;
    else if (errored || stillDirty) message = core.saveState.message || SEND_FAILED_MESSAGE;
    else if (failedPhotos > 0) message = FAILED_PHOTOS_MESSAGE;
    else if (pendingPhotos > 0) {
      message = pendingPhotos === 1
        ? '1 photo is still sending. Stay in signal until it has a tick, then save again.'
        : `${pendingPhotos} photos are still sending. Stay in signal until they have a tick, then save again.`;
    }

    return summary({
      ok: message === null,
      message,
      offline,
      stale,
      conflicts: conflicts.length,
      failed: failed.length,
      pendingPhotos,
      failedPhotos
    });
  }

  /**
   * Resolves a conflict: 'theirs' takes the server's; 'mine' saves this
   * phone's over it (for a work-type change, clearing the answered forms);
   * 'both' (notes only) joins the two texts, each with its author's name.
   */
  async function resolveConflict(key, choice) {
    const partKey = lower(key);
    const conflict = core.conflicts[partKey];
    const entry = core.local[partKey];
    if (!conflict || !entry) return { ok: false, message: 'There is nothing to choose for that part now.' };
    delete core.conflicts[partKey];

    if (choice === 'theirs') {
      delete core.local[partKey];
      await forgetEntry(entry);
      emit();
      return { ok: true };
    }

    if (choice === 'both' && conflict.canKeepBoth) {
      const theirText = String((conflict.theirs && conflict.theirs.value && conflict.theirs.value.notes) || '').trim();
      const myText = String((entry.value && entry.value.notes) || '').trim();
      const myName = props().userName || 'Me';
      const joined = [theirText ? `${conflict.theirName}: ${theirText}` : '', myText ? `${myName}: ${myText}` : '']
        .filter(Boolean).join('\n\n');
      entry.value = { ...(entry.value || {}), notes: joined };
    } else if (choice !== 'mine' && choice !== 'both') {
      core.conflicts[partKey] = conflict;
      return { ok: false, message: 'Choose which one to keep.' };
    }

    let notice = null;
    if (conflict.kind === 'work_type') {
      entry.clearsRobotAnswers = true;
    } else {
      if (conflict.theirs) entry.baseVersion = Number(conflict.theirs.version) || entry.baseVersion;
      if (isFormKey(partKey) && conflict.theirs && hasAnswers(conflict.theirs.value)
        && !sameJson(entry.value && entry.value.form, conflict.theirs.value.form)) {
        notice = `${conflict.theirName}'s answers for ${conflict.label} are kept for reference in the saved Log Work.`;
      }
    }
    entry.state = 'dirty';
    entry.unconfirmed = null;
    entry.changedAt = nowIso();
    await persistEntry(entry);
    emit();
    const outcome = await sendPart(partKey);
    if (outcome === 'saved') {
      if (notice) {
        core.saveState = { ...core.saveState, message: notice };
        emit();
      }
      if (conflict.kind === 'work_type') quietRefresh();
      return { ok: true, message: notice };
    }
    return { ok: false, outcome, message: core.conflicts[partKey] ? core.conflicts[partKey].message : null };
  }

  /**
   * A stale copy: 'show' hands its changes to the screen (and takes it off
   * Today's list), 'copy' saves them into the active Log Work on top of the
   * versions the GET gave, 'discard' throws them away.
   *
   * Parts used lines (spec 6.5): a copy holding any stays on the phone until
   * Discard or a successful Copy, even after Show, so a reload never loses a
   * fitted part. Copy keeps each line's ORIGINAL key, whatever its version:
   * the phone cannot tell a line whose save reached the server (reply lost,
   * then filed by the lead) from one that never arrived, and the filing
   * skips and names a line already recorded on this job, so the same key can
   * never be recorded twice. A line removed on the phone is not copied. On a
   * finished job nothing can be copied: the copy is kept for the job page.
   */
  async function resolveStale(id, choice) {
    const copy = core.stale.find(item => item.id === id);
    if (!copy) return { ok: false, message: 'Those changes are no longer on this phone.' };
    const { userId, taskId } = props();
    const store = phone();
    const forgetCopy = async () => {
      for (const entry of copy.parts) {
        try { await store.deletePart(userId, taskId, entry.draftId, entry.partKey); } catch (error) { /* harmless */ }
      }
      for (const photo of copy.photos) await forgetPhoto(photo.clientRef);
    };
    const holdsSpareLines = copy.parts.some(entry => isSpareKey(entry.partKey) && entry.value);

    if (choice === 'show') {
      if (!holdsSpareLines) await forgetCopy();
      copy.shown = true;
      emit();
      return {
        ok: true,
        parts: copy.parts.map(entry => ({ partKey: entry.partKey, label: labelFor(entry.partKey, entry.value), value: clone(entry.value), changedAt: entry.changedAt })),
        photos: copy.photos.map(photo => ({ client_ref: photo.clientRef, name: photo.name, blob: photo.blob }))
      };
    }
    if (choice === 'discard') {
      await forgetCopy();
      core.stale = core.stale.filter(item => item !== copy);
      emit();
      return { ok: true };
    }
    if (choice === 'copy') {
      if (core.jobFinished) {
        // Sent again it would only meet JOB_FINISHED and come back as a stale copy.
        return {
          ok: false,
          message: holdsSpareLines
            ? 'This job is finished, so nothing can be copied into its Log Work. Ask a team leader to add the parts on the job page.'
            : 'This job is finished, so nothing can be copied into its Log Work.'
        };
      }
      if (core.status !== 'ready') return { ok: false, message: NO_SIGNAL_SAVE_MESSAGE };
      await forgetCopy();
      core.stale = core.stale.filter(item => item !== copy);
      const copiedSpare = [];
      for (const old of copy.parts) {
        if (isSpareKey(old.partKey)) {
          // A line removed on the phone has nothing to record.
          if (!old.value) continue;
          copiedSpare.push(old.value.name || 'A part');
        }
        const existing = core.local[old.partKey];
        if (existing) {
          existing.value = clone(old.value);
          existing.changedAt = nowIso();
          if (existing.state === 'failed') existing.state = 'dirty';
          await persistEntry(existing);
          continue;
        }
        const serverPart = core.parts[old.partKey];
        const entry = {
          partKey: old.partKey,
          value: clone(old.value),
          baseVersion: serverPart ? Number(serverPart.version) || 0 : 0,
          draftId: core.draftId,
          storedDraftId: undefined,
          openedAt: core.openedAt,
          changedAt: nowIso(),
          state: 'dirty',
          failMessage: null,
          clearsRobotAnswers: false,
          unconfirmed: null
        };
        core.local[old.partKey] = entry;
        await persistEntry(entry);
      }
      for (const old of copy.photos) {
        const photo = {
          ...old,
          clientRef: newUuid(),
          draftId: core.draftId,
          state: 'waiting',
          failMessage: null,
          cancelled: false
        };
        const stored = await persistPhoto(photo, 'pending');
        if (stored && stored.ok === false && stored.error === 'quota') continue;
        core.photos.push(photo);
      }
      emit();
      const results = await sendAllDirty();
      pumpPhotos();
      return { ok: !results.some(outcome => outcome !== 'saved'), outcomes: results, copiedSpare };
    }
    return { ok: false, message: 'Choose Show, Copy into today\'s Log Work or Discard.' };
  }

  /** Throws away a part change that the server refused (Discard); a failed photo goes through removeFile. */
  async function discardFailed(key) {
    const partKey = lower(key);
    const entry = core.local[partKey];
    if (entry && entry.state === 'failed') {
      delete core.local[partKey];
      await forgetEntry(entry);
      emit();
      return { ok: true };
    }
    const photo = core.photos.find(item => item.clientRef === key && item.state === 'failed');
    if (photo) return removeFile(photo.clientRef);
    return { ok: false };
  }

  /** { partKey: version } for every part the screen holds, archived answers left out. */
  function versionsForFiling() {
    const versions = {};
    for (const [key, part] of Object.entries(core.parts)) {
      if (isArchiveKey(key)) continue;
      versions[key] = Number(part.version) || 0;
    }
    return versions;
  }

  function fileIdsForFiling() {
    return core.files.map(file => file.file_id).filter(Boolean);
  }

  /** The saved photos and files of one part (and step), as the Complete Work Log body carries them. */
  function attachmentsFor(partKey, stepKey) {
    const key = lower(partKey);
    return core.files
      .filter(file => file.part_key === key && (stepKey === undefined || (file.step_key || null) === (stepKey || null)))
      .map(file => ({
        file_id: file.file_id,
        client_ref: file.client_ref,
        url: file.url,
        name: file.name,
        size: file.size,
        type: file.type,
        step_key: file.step_key || null
      }));
  }

  /** After Complete Work Log answered 201 (or 200 already_saved): the phone's copy of this draft goes. */
  async function markFiled() {
    const { userId, taskId } = props();
    const draftId = core.draftId;
    Object.values(core.timers.debounce).forEach(id => clearTimeout(id));
    core.timers.debounce = {};
    core.local = {};
    core.conflicts = {};
    core.restoredOlder = [];
    core.removals = [];
    core.photos = core.photos.filter(photo => photo.state === 'sending');
    const store = phone();
    try {
      // Stale copies stay: this Complete Work Log did not file them (a Parts used
      // line kept with no signal after someone else saved is one), so only
      // the engineer's Copy or Discard removes them.
      if (draftId) await store.clearTask(userId, taskId, draftId, { keepStale: true });
      await store.clearTask(userId, taskId, null, { keepStale: true });
    } catch (error) {
      // What is left is found stale next time, and never filed twice.
    }
    emit();
    if (core.status === 'ready' || core.status === 'offline') return fetchServer();
    return { ok: true };
  }

  // -------------------------------------------------------------------------
  // Life cycle
  // -------------------------------------------------------------------------

  function addListeners() {
    if (typeof window === 'undefined' || core.listeners) return;
    const gen = core.gen;
    const online = () => {
      if (gen !== core.gen) return;
      if (core.timers.retry) clearTimeout(core.timers.retry);
      core.timers.retry = null;
      core.retryAttempt = 0;
      retryNow();
    };
    const visibility = () => {
      if (gen !== core.gen) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('online', online);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', visibility);
    core.listeners = { online, visibility };
  }

  function removeListeners() {
    if (!core.listeners || typeof window === 'undefined') return;
    window.removeEventListener('online', core.listeners.online);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', core.listeners.visibility);
    core.listeners = null;
  }

  function stop() {
    clearTimers();
    removeListeners();
    core = { ...freshCore(core.gen + 1), status: 'unavailable' };
  }

  async function start() {
    stop();
    const { enabled, taskId, userId, authFetch } = props();
    if (!enabled || !taskId || !userId || typeof authFetch !== 'function') {
      core.status = 'unavailable';
      emit();
      return;
    }
    core.status = 'loading';
    const gen = core.gen;
    addListeners();
    emit();
    await restoreFromPhone(gen);
    if (gen !== core.gen) return;
    await fetchServer();
  }

  return {
    start,
    stop,
    savePart,
    flush,
    refresh: fetchServer,
    uploadFile,
    removeFile,
    resolveConflict,
    resolveStale,
    discardFailed,
    markFiled,
    versionsForFiling,
    fileIdsForFiling,
    attachmentsFor,
    getCore: () => core,
    staleMessage,
    failedItems,
    labelFor
  };
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

/**
 * @param {object}   args
 * @param {Function} args.authFetch       From AuthContext.
 * @param {string}   args.taskId
 * @param {string}   args.userId          The phone copy is kept per user.
 * @param {boolean}  args.enabled         False: status 'unavailable', no calls at all.
 * @param {string}   [args.userName]      For "Keep both" and our own parts' names.
 * @param {object}   [args.assetRefs]     { asset_id: 'BR-002' }, for sentences naming a robot.
 * @param {Function} [args.onRemoteParts] (partKeys, fileKeys) => void, when a poll or refresh brings a change
 *                                        made elsewhere (a colleague, or the same person on another phone).
 *                                        fileKeys: parts whose photos were added or removed elsewhere.
 * @param {object}   [args.phoneStore]    Tests: an injected logWorkPhoneStore.
 * @param {object}   [args.timing]        Tests: { debounceMs, retryDelaysMs, pollMs, openTimeoutMs }.
 *
 * @returns {{
 *   status: 'loading'|'offline'|'unavailable'|'ready',
 *   draft, draftId, lastSavedId, lastSaved, carriedFrom, parts, files, journeys, unfiled, myUnfiledJourneys,
 *   leadLogs, namesYou, robots, hasContent, jobFinished, canClear,
 *   savePart, flush, refresh, uploadFile, removeFile, attachmentsFor,
 *   photos, pendingPhotos, failed, discardFailed,
 *   conflicts, resolveConflict, staleCopies, resolveStale,
 *   versionsForFiling, fileIdsForFiling, markFiled,
 *   saveState: { lastSavedAt, sending, offline, message }, phoneMessage,
 *   remoteChanges: null | { keys, fileKeys, at }
 * }}
 */
export function useSavedLogWork({
  authFetch, taskId, userId, enabled = true, userName = null, assetRefs = null, onRemoteParts = null,
  phoneStore = null, timing = null
} = {}) {
  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);
  const propsRef = useRef(null);
  propsRef.current = {
    authFetch, taskId, userId, enabled: Boolean(enabled), userName, assetRefs, onRemoteParts,
    phoneStore: phoneStore || undefined, timing: timing || undefined
  };

  const engineRef = useRef(null);
  if (!engineRef.current) {
    engineRef.current = createSavedLogWorkEngine(
      () => propsRef.current,
      () => { if (mountedRef.current) setTick(value => value + 1); }
    );
  }
  const engine = engineRef.current;

  useEffect(() => {
    mountedRef.current = true;
    engine.start();
    return () => engine.stop();
  }, [engine, taskId, userId, enabled]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const savePart = useCallback((key, value, options) => engine.savePart(key, value, options), [engine]);
  const flush = useCallback(() => engine.flush(), [engine]);
  const refresh = useCallback(() => engine.refresh(), [engine]);
  const uploadFile = useCallback((args) => engine.uploadFile(args), [engine]);
  const removeFile = useCallback((idOrRef) => engine.removeFile(idOrRef), [engine]);
  const resolveConflict = useCallback((key, choice) => engine.resolveConflict(key, choice), [engine]);
  const resolveStale = useCallback((id, choice) => engine.resolveStale(id, choice), [engine]);
  const discardFailed = useCallback((key) => engine.discardFailed(key), [engine]);
  const markFiled = useCallback(() => engine.markFiled(), [engine]);
  const versionsForFiling = useCallback(() => engine.versionsForFiling(), [engine]);
  const fileIdsForFiling = useCallback(() => engine.fileIdsForFiling(), [engine]);
  const attachmentsFor = useCallback((partKey, stepKey) => engine.attachmentsFor(partKey, stepKey), [engine]);

  const view = useMemo(() => {
    const core = engine.getCore();
    const myId = lower(userId);
    const parts = {};
    for (const [key, part] of Object.entries(core.parts)) parts[key] = { ...part };
    for (const [key, entry] of Object.entries(core.local)) {
      const base = parts[key] || {
        version: 0, changed_by: myId, changed_by_name: userName || null, changed_at: entry.changedAt, carried: false
      };
      parts[key] = {
        ...base,
        value: entry.value,
        local: true,
        pending: entry.state === 'dirty',
        failed: entry.state === 'failed',
        conflict: entry.state === 'conflict'
      };
    }
    const draft = core.draft;
    const localContent = Object.values(core.local).some(entry => entry.value !== null && entry.value !== undefined)
      || core.photos.length > 0;
    const photos = core.photos.map(photo => ({
      client_ref: photo.clientRef,
      part_key: photo.partKey,
      step_key: photo.stepKey,
      name: photo.name,
      size: photo.size,
      type: photo.type,
      blob: photo.blob,
      state: photo.state,
      fail_message: photo.failMessage || null
    }));
    return {
      status: core.status,
      draft,
      draftId: core.draftId,
      lastSavedId: (core.lastSaved && core.lastSaved.draft_id) || null,
      lastSaved: core.lastSaved,
      carriedFrom: draft ? draft.carried_from || null : null,
      parts,
      files: core.files,
      journeys: draft && Array.isArray(draft.journeys) ? draft.journeys : [],
      unfiled: core.unfiled,
      myUnfiledJourneys: core.myUnfiled,
      leadLogs: core.leadLogs,
      namesYou: Boolean(draft && draft.names_you),
      robots: draft && draft.robots ? draft.robots : { total: 0, done: 0 },
      hasContent: Boolean(draft && draft.has_content) || localContent,
      jobFinished: core.jobFinished,
      canClear: core.canClear,
      photos,
      pendingPhotos: photos.filter(photo => photo.state === 'waiting' || photo.state === 'sending'),
      failed: engine.failedItems(),
      conflicts: Object.values(core.conflicts).map(conflict => ({ ...conflict })),
      staleCopies: core.stale.map(copy => ({
        id: copy.id,
        draftId: copy.draftId,
        code: copy.code,
        shown: copy.shown,
        message: engine.staleMessage(copy),
        parts: copy.parts.map(entry => ({
          partKey: entry.partKey, label: engine.labelFor(entry.partKey, entry.value), value: entry.value, changedAt: entry.changedAt
        })),
        photos: copy.photos.map(photo => ({ client_ref: photo.clientRef, name: photo.name }))
      })),
      saveState: { ...core.saveState },
      phoneMessage: core.phoneMessage,
      remoteChanges: core.remoteChanges
    };
    // tick is the engine's change counter; the engine itself never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, tick, userId, userName]);

  return {
    ...view,
    savePart,
    flush,
    refresh,
    uploadFile,
    removeFile,
    attachmentsFor,
    discardFailed,
    resolveConflict,
    resolveStale,
    versionsForFiling,
    fileIdsForFiling,
    markFiled
  };
}

export default useSavedLogWork;
